import React, { useEffect, useRef, useState } from 'react';
import { canImportHistory } from '../lib/history-capability';
import {
  getSessions,
  startHistoryBackfill,
  listHistoryBackfill,
} from '../api.js';

// 3-month hard ceiling (mirrors backend HISTORY_BACKFILL_MAX_DAYS).
const MAX_BACKFILL_DAYS = 90;

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const STATUS_LABEL = {
  pending: { text: 'Ожидает запуска', cls: 'badge-info' },
  running: { text: 'Загружается…', cls: 'badge-warn' },
  completed: { text: 'Завершено', cls: 'badge-ok' },
  failed: { text: 'Ошибка', cls: 'badge-error' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function HistoryBackfill() {
  const [sessions, setSessions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ack, setAck] = useState(false);
  // Calendar choice: import messages on/after this date. Default 30d back.
  const [sinceDate, setSinceDate] = useState(isoDaysAgo(30));
  const [toast, setToast] = useState(null);

  const loadJobs = () => listHistoryBackfill().then(setJobs).catch(() => {});
  const loadSessions = () => getSessions().then(setSessions).catch(() => {});

  useEffect(() => {
    loadSessions();
    loadJobs();
  }, []);

  // Fast 3s polling only while a job is actually moving; an idle open tab
  // otherwise fires ~28k requests/day for a table that never changes. The
  // slow 30s tick still picks up jobs started from another tab/session.
  const hasActiveJob = jobs.some(
    (j) => j.status === 'pending' || j.status === 'running',
  );
  useEffect(() => {
    const t = setInterval(loadJobs, hasActiveJob ? 3000 : 30000);
    return () => clearInterval(t);
  }, [hasActiveJob]);

  // Only history-capable personal sessions can import whole-account history.
  // The list is shared so the backend and this button cannot drift apart.
  const tgSessions = sessions.filter(canImportHistory);

  // Cancel the previous dismiss timer so back-to-back toasts don't get
  // wiped early by a stale timer, and clean up on unmount (same fix
  // Channels.jsx carries).
  const toastTimer = useRef(null);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const notify = (text, type = 'info') => {
    setToast({ text, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const openConfirm = () => {
    if (!selectedId) { notify('Выберите номер', 'error'); return; }
    setAck(false);
    setConfirmOpen(true);
  };

  // Escape closes the confirm popup — keyboard parity with the Channels
  // modal, which added the same handler deliberately.
  useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmOpen]);

  const run = async (pushToBitrix) => {
    try {
      await startHistoryBackfill(selectedId, { sinceDate, pushToBitrix });
      setConfirmOpen(false);
      notify(
        pushToBitrix
          ? 'Загрузка запущена — сообщения уйдут и в Открытые линии'
          : 'Загрузка запущена — история только в Связке, без Открытых линий',
        'ok',
      );
      loadJobs();
    } catch (err) {
      const detail = String(err.message || '').replace(/^\d+:\s*/, '');
      notify(`Не удалось запустить: ${detail}`, 'error');
    }
  };

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <h1>Администрирование истории</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
          Импорт переписки из Telegram по всем чатам аккаунта за заданный период.
          Доступно только для личных Telegram-номеров.
        </p>
      </div>

      {toast && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 14,
          background: toast.type === 'error' ? '#fef2f2' : toast.type === 'ok' ? '#f0fdf4' : '#eff6ff',
          color: toast.type === 'error' ? '#991b1b' : toast.type === 'ok' ? '#166534' : '#1e40af',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : toast.type === 'ok' ? '#bbf7d0' : '#bfdbfe'}`,
        }}>{toast.text}</div>
      )}

      {/* Launch panel */}
      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Number */}
          <div style={{ flex: '0 1 300px', minWidth: 200 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Номер (Telegram Personal)
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{ width: '100%', height: 42, padding: '0 12px', fontSize: 14, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', boxSizing: 'border-box' }}
            >
              <option value="">— выберите номер —</option>
              {tgSessions.map((s) => (
                <option key={s.id} value={s.id}>{s.phone_number}</option>
              ))}
            </select>
            {tgSessions.length === 0 && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                Нет подключённых Telegram-номеров.
              </div>
            )}
          </div>

          {/* Since date */}
          <div style={{ flex: '0 0 190px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Загружать сообщения с
            </label>
            <input
              type="date"
              value={sinceDate}
              min={isoDaysAgo(MAX_BACKFILL_DAYS)}
              max={isoDaysAgo(0)}
              onChange={(e) => setSinceDate(e.target.value)}
              style={{ width: '100%', height: 42, padding: '0 12px', fontSize: 14, borderRadius: 8, border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
              Не раньше 3 месяцев назад
            </div>
          </div>

          {/* Submit — hidden label spacer lines the button up with the inputs */}
          <div style={{ flex: '0 0 auto' }}>
            <label aria-hidden="true" style={{ display: 'block', fontSize: 13, marginBottom: 6, visibility: 'hidden', userSelect: 'none' }}>
              &nbsp;
            </label>
            <button
              className="btn-primary"
              onClick={openConfirm}
              disabled={!selectedId}
              style={{ height: 42, padding: '0 22px', fontSize: 14, boxSizing: 'border-box' }}
            >
              Подтянуть историю
            </button>
          </div>
        </div>
      </div>

      {/* Jobs table */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Номер</th>
              <th>Статус</th>
              <th>Период</th>
              <th>Прогресс</th>
              <th>В Открытые линии</th>
              <th>Начато</th>
              <th>Завершено</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>
                Загрузок ещё не было
              </td></tr>
            )}
            {jobs.map((j) => {
              const st = STATUS_LABEL[j.status] || { text: j.status, cls: 'badge-info' };
              return (
                <tr key={j.id}>
                  <td><strong>{j.phone_number}</strong></td>
                  <td><span className={`badge ${st.cls}`}>{st.text}</span></td>
                  <td>{j.period_days} дн.</td>
                  <td style={{ fontSize: 13 }}>
                    {j.dialogs_done}/{j.dialogs_total ?? '?'} чатов · {j.messages_done} сообщ.
                    {j.error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>⚠ {j.error}</div>}
                  </td>
                  <td>{j.push_to_bitrix ? 'да' : 'нет'}</td>
                  <td style={{ fontSize: 13 }}>{fmtDate(j.started_at)}</td>
                  <td style={{ fontSize: 13 }}>{fmtDate(j.finished_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm popup */}
      {confirmOpen && (
        <div
          onClick={() => setConfirmOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 520, width: '90%' }}
          >
            <h2 style={{ marginTop: 0, fontSize: 20 }}>Загрузка истории</h2>
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: 14, fontSize: 14, color: '#78350f', lineHeight: 1.5, marginBottom: 16,
            }}>
              История подтянется по <strong>всем чатам</strong> аккаунта, начиная с {sinceDate}.
              Если загрузить <strong>с пушем в Открытые линии</strong>, все эти сообщения
              уйдут в Битрикс <strong>как новые</strong> — операторов может завалить
              тысячами старых сообщений. Чтобы просто увидеть историю в Связке без Битрикс24,
              используйте «Загрузить без Открытых линий».
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.45, marginBottom: 20, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                style={{ width: 18, height: 18, flexShrink: 0, margin: '1px 0 0' }}
              />
              <span>Я понимаю, что при пуше история уйдёт в Открытые линии как новые сообщения.</span>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn-primary"
                disabled={!ack}
                onClick={() => run(true)}
                title={ack ? '' : 'Отметьте галочку подтверждения'}
                style={{ width: '100%', padding: '11px 16px' }}
              >
                Подтвердить (с пушем)
              </button>
              <button className="btn-ghost" onClick={() => run(false)} style={{ width: '100%', padding: '11px 16px' }}>
                Загрузить без Открытых линий
              </button>
              <button className="btn-ghost" onClick={() => setConfirmOpen(false)} style={{ width: '100%', padding: '11px 16px' }}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
